/**
 * @typedef {Object} HoverIntentOptions
 * @property {number} [sensitivity]
 * @property {number} [interval]
 * @property {number} [timeout]
 * @property {boolean} [handleFocus]
 */

/**
 * @typedef {Object} HoverIntentAPI
 * @property {(opts: HoverIntentOptions) => HoverIntentAPI} options
 * @property {() => void} remove
 */

interface HoverIntentOptions {
    sensitivity?: number;
    interval?: number;
    timeout?: number;
    handleFocus?: boolean;
}

interface HoverIntentAPI {
    options(opts: HoverIntentOptions): HoverIntentAPI;
    remove(): void;
}

type HoverIntentHandler = (this: HTMLElement, e: MouseEvent | FocusEvent) => void;

export function hoverintent(
    element: HTMLElement,
    over: HoverIntentHandler,
    out: HoverIntentHandler
): HoverIntentAPI {
    let mouseX = 0, mouseY = 0, prevX = 0, prevY = 0;
    let state = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let focusActive = false;
    let pointerActive = false;

    let options: HoverIntentOptions = {
        sensitivity: 7,
        interval: 100,
        timeout: 0,
        handleFocus: false
    };

    function trackMouse(e: MouseEvent) {
        mouseX = e.clientX;
        mouseY = e.clientY;
    }

    function compare(e: MouseEvent) {
        if (timer) clearTimeout(timer);
        if (Math.abs(prevX - mouseX) + Math.abs(prevY - mouseY) < (options.sensitivity ?? 7)) {
            state = 1;
            if (!pointerActive) over.call(element, e);
        } else {
            prevX = mouseX;
            prevY = mouseY;
            timer = setTimeout(() => compare(e), options.interval ?? 100);
        }
    }

    function onMouseOver(e: MouseEvent) {
        focusActive = true;
        if (timer) clearTimeout(timer);
        if (state !== 1) {
            prevX = e.clientX;
            prevY = e.clientY;
            element.addEventListener('mousemove', trackMouse, false);
            timer = setTimeout(() => compare(e), options.interval ?? 100);
        }
    }

    function onMouseOut(e: MouseEvent) {
        focusActive = false;
        if (timer) clearTimeout(timer);
        element.removeEventListener('mousemove', trackMouse, false);
        if (state === 1) {
            timer = setTimeout(() => {
                state = 0;
                out.call(element, e);
            }, options.timeout ?? 0);
        }
    }

    function onFocus(e: FocusEvent) {
        if (!focusActive) {
            pointerActive = true;
            over.call(element, e);
        }
    }

    function onBlur(e: FocusEvent) {
        if (pointerActive) {
            pointerActive = false;
            out.call(element, e);
        }
    }

    function addFocusListeners() {
        element.addEventListener('focus', onFocus, false);
        element.addEventListener('blur', onBlur, false);
    }

    function removeFocusListeners() {
        element.removeEventListener('focus', onFocus, false);
        element.removeEventListener('blur', onBlur, false);
    }

    const api: HoverIntentAPI = {
        options(opts: HoverIntentOptions) {
            const focusChanged = opts.handleFocus !== undefined && opts.handleFocus !== options.handleFocus;
            options = { ...options, ...opts };
            if (focusChanged) {
                if (options.handleFocus) addFocusListeners();
                else removeFocusListeners();
            }
            return api;
        },
        remove() {
            element.removeEventListener('mouseover', onMouseOver, false);
            element.removeEventListener('mouseout', onMouseOut, false);
            removeFocusListeners();
        }
    };

    element.addEventListener('mouseover', onMouseOver, false);
    element.addEventListener('mouseout', onMouseOut, false);

    return api;
}